import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Container } from '@/components/ui/container';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/toast/index';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { supabase } from '@/integrations/supabase/client';
import { Helmet } from 'react-helmet';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { useAuth } from '@/context/AuthContext'; // Import useAuth

// Validation Schema
const passwordResetSchema = z.object({
  password: z.string()
    .min(6, "Fjalëkalimi duhet të jetë të paktën 6 karaktere")
    .max(64, "Fjalëkalimi duhet të jetë më pak se 64 karaktere"),
  confirmPassword: z.string()
}).refine(data => data.password === data.confirmPassword, {
  message: "Fjalëkalimet nuk përputhen",
  path: ["confirmPassword"],
});

const ResetPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isSessionVerified, setIsSessionVerified] = useState<boolean | null>(null);
  const [verificationAttempted, setVerificationAttempted] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation(); // Provides access to location.search and location.hash
  const { session: authSession, user, isLoading: authIsLoading } = useAuth();

  const isNavigatingRef = useRef(false);
  const isMountedRef = useRef(true);

  const form = useForm<z.infer<typeof passwordResetSchema>>({
    resolver: zodResolver(passwordResetSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (isNavigatingRef.current || !isMountedRef.current || isComplete || verificationAttempted) {
      return;
    }

    if (authIsLoading) {
      // If AuthContext is still loading, wait. Spinner will be shown based on isSessionVerified === null.
      return;
    }

    setVerificationAttempted(true);

    let paramsSource: 'hash' | 'search' | null = null;
    let parsedParams: URLSearchParams | null = null;

    // Try to get params from hash first (for PKCE flow)
    if (location.hash && location.hash.includes('type=recovery')) {
      const hashParams = new URLSearchParams(location.hash.substring(1)); // Remove #
      if (hashParams.get('code') && hashParams.get('type') === 'recovery') {
        parsedParams = hashParams;
        paramsSource = 'hash';
        console.log("ResetPassword Effect: Found recovery params in HASH:", parsedParams.toString());
      }
    }

    // Fallback to search params if not found in hash
    if (!parsedParams && location.search) {
      const searchParams = new URLSearchParams(location.search);
      if (searchParams.get('code') && searchParams.get('type') === 'recovery') {
        parsedParams = searchParams;
        paramsSource = 'search';
        console.log("ResetPassword Effect: Found recovery params in SEARCH:", parsedParams.toString());
      }
    }
    
    const code = parsedParams ? parsedParams.get('code') : null;
    const type = parsedParams ? parsedParams.get('type') : null;

    console.log("ResetPassword Effect: authIsLoading:", authIsLoading, "authSession:", !!authSession, "user:", !!user, "code from URL:", code, "type from URL:", type, "paramsSource:", paramsSource);

    if (type === 'recovery' && code) {
      // Recovery flow triggered by URL parameters (hash or search).
      // Supabase client (via AuthContext) should auto-exchange PKCE code if it's in the hash.
      // We check authSession and user to see if the exchange was successful.
      if (authSession && user) {
        console.log("ResetPassword Effect: Recovery flow, session and user exist. Verifying session.");
        setIsSessionVerified(true);
        // Clean the URL parameters as they have been processed
        if (window.history.replaceState) {
          const url = new URL(window.location.href);
          if (paramsSource === 'hash') {
            url.hash = ''; // Clear the hash
          }
          if (paramsSource === 'search') { // Clear specific search params
            url.searchParams.delete('code');
            url.searchParams.delete('type');
          }
          // Clear any remaining relevant params if needed, or simplify to:
          // url.search = ''; url.hash = ''; if all recovery related params should be gone.
          // For now, this targeted clearing should be sufficient.
          window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
        }
      } else {
        console.log("ResetPassword Effect: Recovery flow, but no session/user yet. PKCE exchange might have failed or is in progress by Supabase.");
        // If URL params for recovery are present, but AuthContext doesn't have a session/user,
        // this could mean the PKCE code exchange failed (invalid/expired code) or Supabase client hasn't finished.
        // The Supabase client handles the #access_token part. If it fails, it usually redirects or fails silently.
        // We rely on authSession and user to confirm success.
        // If they are not present after authIsLoading is false, it's likely a failure.
        setIsSessionVerified(false);
        if (!isNavigatingRef.current) {
          isNavigatingRef.current = true;
          toast({
            title: "Gabim në rivendosjen e fjalëkalimit",
            description: "Linku për rivendosje të fjalëkalimit mund të ketë skaduar, është i pavlefshëm, ose ndodhi një gabim gjatë verifikimit. Provoni të kërkoni një link të ri.",
            variant: "destructive",
          });
          setTimeout(() => {
            if (isMountedRef.current) navigate('/auth', { replace: true });
          }, 4000); // Increased timeout for user to read message
        }
      }
    } else {
      // Not in a recovery flow via URL parameters (no code/type=recovery).
      // If there's an existing session (user navigated here while logged in), allow password change.
      // If no session, it's an invalid access.
      if (authSession && user) {
        console.log("ResetPassword Effect: Not recovery flow by URL, but session and user exist. Verifying session.");
        setIsSessionVerified(true);
      } else {
        console.log("ResetPassword Effect: Not recovery flow by URL, no session/user. Invalid access.");
        setIsSessionVerified(false);
        if (!isNavigatingRef.current) {
          isNavigatingRef.current = true;
          toast({
            title: "Gabim",
            description: "Qasje e paautorizuar ose link i pavlefshëm për rivendosjen e fjalëkalimit. Ju lutemi përdorni linkun e dërguar në email.",
            variant: "destructive",
          });
          setTimeout(() => {
            if (isMountedRef.current) navigate('/auth', { replace: true });
          }, 4000); // Increased timeout
        }
      }
    }
  }, [authSession, user, authIsLoading, location, navigate, toast, isComplete, verificationAttempted]); // Using location to react to hash/search changes


  const onSubmit = async (values: z.infer<typeof passwordResetSchema>) => {
    if (isNavigatingRef.current || !isMountedRef.current) return;

    if (!isSessionVerified) {
      console.error("ResetPassword Submit: Attempted submit with unverified session.");
      if (!isNavigatingRef.current) { 
        toast({
          title: "Verifikimi dështoi",
          description: "Sesioni nuk është verifikuar. Ju lutemi provoni linkun e rivendosjes përsëri ose kërkoni një link të ri.",
          variant: "destructive",
        });
      }
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: values.password,
      });
      if (error) throw error;

      if (!isMountedRef.current || isNavigatingRef.current) return;
      
      setIsComplete(true);
      isNavigatingRef.current = true; 
      toast({
        title: "Fjalëkalimi u ndryshua!",
        description: "Fjalëkalimi juaj është rivendosur me sukses.",
      });
      setTimeout(() => {
        if (isMountedRef.current) {
          // Sign out the user after successful password reset to force a new login with the new password
          supabase.auth.signOut().then(() => {
            if (isMountedRef.current) navigate('/auth', { replace: true });
          }).catch(signOutError => {
            console.error("Error signing out after password reset:", signOutError);
            // Still navigate to auth page even if sign out fails locally
            if (isMountedRef.current) navigate('/auth', { replace: true });
          });
        }
      }, 2000);
    } catch (error: any) {
      console.error("ResetPassword Submit Error:", error);
      if (isMountedRef.current && !isNavigatingRef.current) {
        let description = error.message || "Ndodhi një gabim gjatë rivendosjes së fjalëkalimit tuaj.";
        if (error.message && error.message.includes("New password should be different from the old password.")) {
            description = "Fjalëkalimi i ri duhet të jetë ndryshe nga fjalëkalimi i vjetër.";
        } else if (error.message && error.message.includes("Password should be at least 6 characters.")) {
            description = "Fjalëkalimi duhet të jetë të paktën 6 karaktere.";
        }
        toast({
          title: "Ndryshimi i fjalëkalimit dështoi",
          description: description,
          variant: "destructive",
        });
      }
    } finally {
      if (isMountedRef.current && !isComplete) {
        setIsLoading(false);
      }
    }
  };

  if (isSessionVerified === null && !authIsLoading) {
    // If still verifying and auth context is not loading, this means we are waiting for PKCE or manual navigation
    // This state could also occur if the initial useEffect logic determines it's an invalid access before authIsLoading completes.
    // The primary loading spinner for auth context is in AuthProvider or similar.
    // Here, if isSessionVerified is null, it means our logic is determining validity or waiting for URL params.
    // If authIsLoading is also false, and we have code/type in URL, it means PKCE is likely being handled by Supabase client.
    // The LoadingSpinner will show if isSessionVerified is null and we haven't navigated away due to an error.
  }
  
  // Show main loading spinner if AuthContext is loading OR if our session verification is in an indeterminate state (isSessionVerified === null)
  // but only if we haven't already decided it's an error and started navigating.
  if (authIsLoading || (isSessionVerified === null && !isNavigatingRef.current)) {
      return <LoadingSpinner size="lg" className="fixed inset-0 m-auto" />;
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>Rivendosje e Fjalëkalimit | Tregu.al</title>
        <meta name="description" content="Rivendosni fjalëkalimin tuaj për llogarinë në Tregu.al" />
      </Helmet>
      <Navbar />
      <main className="flex-grow pt-16 pb-12 bg-gray-50">
        <Container>
          <div className="max-w-md mx-auto mt-8">
            <Card>
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl font-bold">Rivendosje e Fjalëkalimit</CardTitle>
                <CardDescription>
                  {isSessionVerified ? "Vendosni fjalëkalimin e ri për të rivendosur aksesin në llogarinë tuaj." : "Verifikimi i linkut dështoi."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isComplete ? (
                  <div className="text-center py-6">
                    <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium">Fjalëkalimi u ndryshua!</h3>
                    <p className="text-gray-500 mt-2">
                      Fjalëkalimi juaj është rivendosur me sukses. Po ju ridrejtojmë tek faqja e hyrjes...
                    </p>
                  </div>
                ) : !isSessionVerified ? (
                  <div className="text-center py-6">
                    <div className="h-12 w-12 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium">Lidhja për rivendosjen e fjalëkalimit ka skaduar ose është e pavlefshme</h3>
                    <p className="text-gray-500 mt-2">
                      Kjo mund të ndodhë nëse linku është përdorur tashmë, ka skaduar, ose nuk është kopjuar saktë.
                      Ju lutemi kërkoni një lidhje të re për rivendosjen e fjalëkalimit.
                    </p>
                    <Button 
                      onClick={() => {
                        if (!isNavigatingRef.current) {
                          isNavigatingRef.current = true;
                          navigate('/auth', { replace: true });
                        }
                      }}
                      variant="outline"
                      className="mt-4"
                      disabled={isNavigatingRef.current}
                    >
                      Kthehu tek hyrja
                    </Button>
                  </div>
                ) : (
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                      <FormField
                        control={form.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Fjalëkalimi i Ri</FormLabel>
                            <FormControl>
                              <Input 
                                type="password" 
                                placeholder="Vendosni fjalëkalimin e ri" 
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="confirmPassword"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Konfirmo Fjalëkalimin</FormLabel>
                            <FormControl>
                              <Input 
                                type="password" 
                                placeholder="Konfirmoni fjalëkalimin e ri" 
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        type="submit"
                        className="w-full bg-brand-500 hover:bg-brand-600"
                        disabled={isLoading || isNavigatingRef.current || form.formState.isSubmitting}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Duke përpunuar...
                          </>
                        ) : (
                          "Rivendos Fjalëkalimin"
                        )}
                      </Button>
                    </form>
                  </Form>
                )}
              </CardContent>
            </Card>
          </div>
        </Container>
      </main>
      <Footer />
    </div>
  );
};

export default ResetPassword;
